/*
 *  Copyright (c) 2003-2019 GameDuell GmbH, All Rights Reserved
 *  This document is strictly confidential and sole property of GameDuell GmbH, Berlin, Germany
 */
import { Tournament, TournamentUi } from '@/model/Tournament'
import { CompetitionResult } from '@/model/CompetitionResult'
import { TournamentStatus } from '@/model/TournamentStatus'
import { State } from '../state'
import { getTournamentType, isMasters, createGamesUi, formatMetaData } from '../lib/util'
import GameState from '@/model/GameState'
import { ERROR_CODES } from '@/model/ErrorCodes'
import { Commit } from 'vuex'

export interface TournamentHistoryState {
  zoneId: string
  tournaments: { [id: string]: Tournament }
  // selected tournament
  tournament: Tournament
  games: { [id: string]: CompetitionResult }
  selectedCompetitionResult: CompetitionResult
}

const state: TournamentHistoryState = {
  zoneId: '',
  tournaments: {},
  tournament: {
    tournamentId: '',
    name: '',
    gameVariant: '',
    tournamentRank: 0,
    tournamentScore: 0,
    games: [],
    gametype: '',
    joined: false,
    dates: {
      competitionEnd: '',
      competitionStart: '',
      registrationEnd: '',
      registrationStart: '',
      lobbyEnd: '',
      lobbyStart: '',
      secondsUntilLobbyStart: -1,
      secondsUntilLobbyEnd: -1,
    },
    metaData: {
      distributionSchema: '',
      entryFee: '',
      numberOfGames: 0,
      numberOfTickets: 0,
      playersPerGame: 0,
      restartFee: '',
      roundsPerGame: 0,
      totalWin: '',
      gameTechs: [],
      image: '',
      rejoinsUnlimited: true,
      rejoinsRemaining: 1,
      clearancePhase: false,
    },
  },
  games: {},
  selectedCompetitionResult: {
    gametype: '',
    nextGame: false,
    ranking: [],
    tournamentId: '',
    state: GameState.FINISHED,
    zoneId: '',
    gameTechs: [],
  },
}

export const getters = {
  tournamentUi: (state: TournamentHistoryState, getters: any, rootState: State): TournamentUi => {
    const games = createGamesUi(state.tournament.games, state.tournament.metaData.numberOfGames)
    return {
      ...state.tournament,
      metaData: formatMetaData(state.tournament.metaData, rootState.user.currency, state.tournament.gametype),
      tournamentType: getTournamentType(state.tournament.gametype, state.zoneId),
      isMasters: isMasters(state.zoneId),
      isRegistrationPossible: false,
      tournamentStatus: TournamentStatus.FINISHED,
      // we don't differentiate between normal and internal
      zoneId: state.zoneId.replace('_gd', ''),
      games,
      // all games are played ( is not the same as tournamentType.FINISHED )
      allGamesPlayed: true,
      // it contains games to play (keep in mind that exists combination of 11 from 12 games are played and the 12th is pending)
      hasNextGame: false,
    }
  },
}

export const actions = {
  // TODO actuall we don't need the following call - the history is only available inside the zone - so no need
  // to fetch the zoneId before
  // Maybe we will need this later when we show history tournaments from outside
  //
  // fetchZoneIdByTournamentId({ commit }: { commit: Commit }, tournamentId: string) {
  //   return new Promise((res, rej) => {
  //     fetch(`${process.env.VUE_APP_REST_URL}history/zone/${tournamentId}`, {
  //   credentials: 'same-origin',
  //   cache: 'no-cache'
  // })
  //       .then(res => res.json())
  //       .then((data: ZoneBase) => {
  //         res(data)
  //       })
  //       .catch((err: Error) => {
  //         console.warn(err)
  //         rej({ errorCode: 'zone-not-ascertainable' })
  //       })
  //   })
  // },
  fetchTournamentHistoryById({ commit }: { commit: Commit }, tournamentId: string) {
    // TODO handle lazy loading and check if tournamentId already exists
    return new Promise((res: (value?: unknown) => void, rej: (reason?: unknown) => void) => {
      fetch(`${process.env.VUE_APP_REST_URL}history/${tournamentId}`, {
        credentials: 'same-origin',
        cache: 'no-cache',
      })
        .then((res) => {
          if (res.status >= 400) {
            commit('error', { id: 'fetchTournamentHistoryById', responseStatus: res.status }, { root: true })
            throw { errorCode: res.status === 401 ? ERROR_CODES.UNAUTHORIZED : ERROR_CODES.UNKNOWN }
          }
          return res.json()
        })
        .then((data: Tournament) => {
          commit('storeTournamentById', { tournamentId, data })
          commit('setTournament', data)
          // console.log(`Stored Tournament data \n
          //   ${JSON.stringify(data)} \n
          //   for tournament-id ${tournamentId}\n
          //   into store.history.tournaments[${tournamentId}]`)
          res()
        })
        .catch((err: Error) => {
          rej(err)
        })
    })
  },
  fetchGameHistoryById({ commit, state }: { commit: Commit; state: TournamentHistoryState }, competitionId: string) {
    return new Promise((res: (value?: unknown) => void, rej: (reason?: unknown) => void) => {
      // lazy loading
      if (state.games[competitionId] === undefined) {
        fetch(`${process.env.VUE_APP_REST_URL}history/game/${competitionId}`, {
          credentials: 'same-origin',
          cache: 'no-cache',
        })
          .then((res) => {
            if (res.status >= 400) {
              commit('error', { id: 'fetchGameHistoryById', responseStatus: res.status }, { root: true })
              throw { errorCode: res.status === 401 ? ERROR_CODES.UNAUTHORIZED : ERROR_CODES.UNKNOWN }
            }
            return res.json()
          })
          .then((data: CompetitionResult) => {
            commit('storeGameHistoryById', { competitionId, data })
            commit('selectedCompetitionResult', competitionId)
            console.log(`Stored CompetitionResult data \n
            ${JSON.stringify(data)} \n
            for competition-id ${competitionId}\n
            into store.history.games[${competitionId}]`)
            res()
          })
          .catch((err: Error) => {
            rej(err)
          })
      } else {
        commit('selectedCompetitionResult', competitionId)
        res()
      }
    })
  },
}

export const mutations = {
  zoneId(state: TournamentHistoryState, zoneId: string) {
    state.zoneId = zoneId
  },
  storeTournamentById(
    state: TournamentHistoryState,
    { tournamentId, data }: { tournamentId: string; data: Tournament }
  ) {
    state.tournaments[tournamentId] = data
  },
  setTournament(state: TournamentHistoryState, tournament: Tournament) {
    state.tournament = tournament
  },
  storeGameHistoryById(
    state: TournamentHistoryState,
    { competitionId, data }: { competitionId: string; data: CompetitionResult }
  ) {
    state.games[competitionId] = data
  },
  selectedCompetitionResult(state: TournamentHistoryState, competitionId: string) {
    state.selectedCompetitionResult = state.games[competitionId]
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
